import {
	Alert,
	AlertTitle,
	Box,
	Button,
	Card,
	CardActions,
	CardContent,
	Link,
	Typography,
} from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { FormType } from '@/api/enum/formType.enum';
import { useRef, useState } from 'react';
import EditPreAdmissionDetails from './components/EditPreAdmissionDetailsForm';
import EditClientInformationForm from './components/EditClientInformationForm';
import EditReferrerInformationDetailsForm from './components/EditReferrerInformationForm';
import EditCaseStatusDetailsForm from './components/EditCaseStatusForm';
import EditResultsForm from './components/EditResultsForm';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useNavigate } from 'react-router-dom';
import { selectMyInfo } from '@/pages/auth/authSlice';
import { useSelector } from 'react-redux';
import { useCreatePreAdmissionMutation } from '@/api/pre-admission/preAdmission';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import { useMemberDuplicationCheck } from '@/common/components/member-duplication/useMemberDuplicationCheck';
import ClosedMemberPickerDialog from '@/common/components/member-duplication/ClosedMemberPickerDialog';
import { AdmissionRequestPageDTO } from '@/api/DTO/member-management/admissionRequest';

export default function PreAdmissionCreatePage() {
	const { t } = useTranslation();
	const [type, setType] = useState<FormType>(FormType.create);
	const navigate = useNavigate();
	const [createPreAdmission] = useCreatePreAdmissionMutation();
	const userInfo = useSelector(selectMyInfo);
	const { runDuplicationCheck, duplicationDialogs } =
		useMemberDuplicationCheck();
	// CHA-001 §3.2: applicant is a service-closed member whose record is in the system.
	const [linkedClosedMember, setLinkedClosedMember] =
		useState<AdmissionRequestPageDTO | null>(null);
	// CHA-001 §3.3: service-closed member kept only in Excel (old File no.).
	const [legacyFileNo, setLegacyFileNo] = useState<string | null>(null);
	const [pickerOpen, setPickerOpen] = useState(false);
	const hasPreviousMember = !!linkedClosedMember || !!legacyFileNo;

	const clientInfoRef = useRef<{
		getFormData: () => Promise<any>;
		validateForm: () => Promise<boolean>;
	}>(null);
	const preAdmissionRef = useRef<{
		getFormData: () => Promise<any>;
		validateForm: () => Promise<boolean>;
	}>(null);
	const referrerInfoRef = useRef<{
		getFormData: () => Promise<any>;
		validateForm: () => Promise<boolean>;
	}>(null);
	const caseStatusRef = useRef<{
		getFormData: () => Promise<any>;
		validateForm: () => Promise<boolean>;
	}>(null);
	const resultsRef = useRef<{
		getFormData: () => Promise<any>;
		validateForm: () => Promise<boolean>;
	}>(null);

	const handleSubmit = async () => {
		const results = await Promise.all([
			preAdmissionRef.current?.validateForm() || Promise.resolve(false),
			clientInfoRef.current?.validateForm() || Promise.resolve(false),
			referrerInfoRef.current?.validateForm() || Promise.resolve(false),
			caseStatusRef.current?.validateForm() || Promise.resolve(false),
			resultsRef.current?.validateForm() || Promise.resolve(false),
		]);
		console.log(results);
		if (results.some((result) => result === false)) return;

		const formData = {
			...((await preAdmissionRef.current?.getFormData()) || {}),
			...((await clientInfoRef.current?.getFormData()) || {}),
			...((await referrerInfoRef.current?.getFormData()) || {}),
			...((await caseStatusRef.current?.getFormData()) || {}),
			...((await resultsRef.current?.getFormData()) || {}),
		};

		// The Pre-admission form has no HKID, so only name and phone are compared.
		// The closed-member step is skipped when one was already picked (CHA-001 §3.2 / §3.3).
		const check = await runDuplicationCheck(
			{
				nameZh: formData.nameZh,
				nameEn: formData.nameEn,
				phone: formData.phone,
			},
			{ skipClosedCheck: hasPreviousMember }
		);
		if (!check.proceed) return;

		// `closedUserId` and `legacyFileNo` are mutually exclusive.
		const closedUserId = linkedClosedMember?.id ?? check.closedUserId;
		const response = await createPreAdmission({
			...formData,
			branchId: userInfo?.currentBranch?.id,
			...(legacyFileNo
				? { legacyFileNo }
				: closedUserId
					? { closedUserId }
					: {}),
		});
		if ('data' in response) {
			enqueueSnackbar(t('snackbar.createPreAdmissionSuccess'), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			navigate(`/pre-admission/pre-admission-list`);
		}
	};

	return (
		<>
			<Box sx={{ width: '100%' }}>
				<Box>
					<Typography variant='h4'>
						{t('drawerListItems.newPreAdmission')}
					</Typography>
					{hasPreviousMember ? (
						<Alert
							severity='info'
							sx={{ marginTop: 2 }}
							action={
								<Button
									variant='outlined'
									size='small'
									sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
									onClick={() => {
										setLinkedClosedMember(null);
										setLegacyFileNo(null);
									}}
								>
									{t('memberDuplication.removeSelection')}
								</Button>
							}
						>
							<AlertTitle>
								{t('memberDuplication.previousMemberSelectedTitle')}
							</AlertTitle>
							{linkedClosedMember ? (
								<Trans
									i18nKey='memberDuplication.previousMemberSelected'
									values={{
										fileNo: linkedClosedMember.fileNo,
										name: [linkedClosedMember.nameEn, linkedClosedMember.nameZh]
											.filter(Boolean)
											.join(' '),
									}}
									components={{
										anchor: (
											<Link
												href={`/member-management/member-list/detail/${linkedClosedMember.id}`}
												target='_blank'
												rel='noopener noreferrer'
												color='#1BBED7'
											/>
										),
									}}
								/>
							) : (
								t('memberDuplication.previousMemberExcelSelected', {
									fileNo: legacyFileNo,
								})
							)}
						</Alert>
					) : (
						<Box
							sx={{
								marginTop: 1,
								display: 'flex',
								alignItems: 'center',
								flexWrap: 'wrap',
								columnGap: 1,
							}}
						>
							<Typography variant='body2' color='text.secondary'>
								{t('memberDuplication.previousMemberTips')}
							</Typography>
							<Button
								variant='text'
								size='small'
								sx={{ textTransform: 'none', minWidth: 0, padding: '0 4px' }}
								onClick={() => setPickerOpen(true)}
							>
								{t('memberDuplication.previousMemberInSystem')}
							</Button>
						</Box>
					)}
					<Card sx={{ marginTop: 3 }}>
						<CardContent sx={{ padding: '24px' }}>
							<EditPreAdmissionDetails
								ref={preAdmissionRef}
								type={type}
								setType={setType}
							/>
							<EditClientInformationForm
								ref={clientInfoRef}
								type={type}
								setType={setType}
							/>
							<EditReferrerInformationDetailsForm
								ref={referrerInfoRef}
								type={type}
								setType={setType}
							/>
							<EditCaseStatusDetailsForm
								ref={caseStatusRef}
								type={type}
								setType={setType}
							/>
							<EditResultsForm ref={resultsRef} type={type} setType={setType} />
						</CardContent>

						<CardActions sx={{ justifyContent: 'end', padding: '24px' }}>
							<Button
								onClick={() => {
									navigate('/pre-admission/pre-admission-list');
								}}
								variant='text'
								sx={{ textTransform: 'none' }}
							>
								{t('button.cancel')}
							</Button>
							<Button
								type='submit'
								variant='contained'
								onClick={handleSubmit}
								endIcon={<CheckCircleOutlineIcon />}
							>
								{t('button.complete')}
							</Button>
						</CardActions>
					</Card>
				</Box>

				{duplicationDialogs}
				<ClosedMemberPickerDialog
					open={pickerOpen}
					onClose={() => setPickerOpen(false)}
					onConfirm={(row) => {
						setLinkedClosedMember(row);
						setLegacyFileNo(null);
						setPickerOpen(false);
					}}
					onConfirmLegacy={(fileNo) => {
						setLegacyFileNo(fileNo);
						setLinkedClosedMember(null);
						setPickerOpen(false);
					}}
				/>
			</Box>
		</>
	);
}
